<template>
    <!-- Root click is handled by the parent (@click falls through to this
         single root); no local handler needed. -->
    <div class="notificationCard" :class="{ 'notificationCard--unread': !notification.IsRead }">
        <div class="notificationCard__avatar">
            <img v-if="userData.ProfilePictureURL"
                :src="userData.ProfilePictureURL"
                class="notificationCard__photo"
                alt="">

            <div v-else class="notificationCard__photo notificationCard__photo--initials">
                <span>{{ userData.Initials }}</span>
            </div>

            <span class="notificationCard__type">
                <i :class="notification.IconName"></i>
            </span>
        </div>

        <div class="notificationCard__body">
            <span class="notificationCard__file">EXP. {{ notification.FileNumber }}</span>

            <p class="notificationCard__message">
                {{ notification.User + " " + notification.DisplayMessage }}
            </p>

            <span v-if="notification.NewMessagesCount > 0" class="notificationCard__more">
                +{{ notification.NewMessagesCount }} mensajes
            </span>

            <span class="notificationCard__date">{{ notification.Date }}</span>
        </div>

        <span v-if="!notification.IsRead" class="notificationCard__dot" aria-label="No leída"></span>
    </div>
</template>

<script>
export default {
    name: "NotificationCard",
    props: {
        notification: {
            type: Object,
            required: true
        },
        userData: {
            type: Object,
            required: true
        }
    }
}
</script>

<style scoped>
.notificationCard {
    position: relative;
    display: flex;
    align-items: center;
    gap: 13px;
    padding: 11px 12px;
    margin: 2px 8px;
    border-radius: 12px;
    color: #ffffff;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    /* State only: the row washes on hover / press / unread. */
    transition: background-color 160ms ease;
}

/* Unread rows carry a faint lavender wash (plus the dot) — read at a glance
   without a heavy colored border. */
.notificationCard--unread {
    background-color: rgba(222, 228, 255, 0.05);
}

.notificationCard:active {
    background-color: rgba(222, 228, 255, 0.12);
}

@media (hover: hover) and (pointer: fine) {
    .notificationCard:hover {
        background-color: rgba(222, 228, 255, 0.10);
    }
}

/* ---------- Avatar + type badge ---------- */
.notificationCard__avatar {
    position: relative;
    flex-shrink: 0;
    width: 46px;
    height: 46px;
}

.notificationCard__photo {
    width: 46px;
    height: 46px;
    border-radius: 50%;
    object-fit: cover;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.35);
}

.notificationCard__photo--initials {
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: var(--LativoLightPurple);
    color: var(--LativoDarkPurple);
    font-family: 'SwitzerBold', sans-serif;
    font-size: 17px;
}

/* Type glyph in a lavender chip, ringed in the panel colour so it reads
   cleanly where it overlaps the avatar. */
.notificationCard__type {
    position: absolute;
    right: -2px;
    bottom: -2px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 21px;
    height: 21px;
    border-radius: 50%;
    background-color: var(--LativoLightPurple);
    color: var(--LativoDarkPurple);
    box-shadow: 0 0 0 2px var(--LativoDarkPurple);
}

.notificationCard__type i {
    font-size: 11px;
    margin: 0;
}

/* ---------- Body ---------- */
.notificationCard__body {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.notificationCard__file {
    font-family: 'SwitzerMedium', sans-serif;
    font-size: 11px;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--LativoLightPurple);
}

.notificationCard__message {
    margin: 0;
    font-size: 13px;
    line-height: 1.4;
    color: rgba(255, 255, 255, 0.9);
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2; /* Truncate after two lines */
    overflow: hidden;
    text-overflow: ellipsis;
}

.notificationCard__more {
    margin-top: 1px;
    font-family: 'SwitzerMedium', sans-serif;
    font-size: 11.5px;
    color: var(--LativoLightPurple);
}

.notificationCard__date {
    margin-top: 1px;
    font-size: 11.5px;
    color: rgba(255, 255, 255, 0.55);
}

/* ---------- Unread marker ---------- */
/* A solid blue dot (distinct from the lavender accent) marks a new / unread
   item — no glow, so it reads as a clean status dot rather than a light. */
.notificationCard__dot {
    flex-shrink: 0;
    width: 9px;
    height: 9px;
    margin-left: 6px;
    border-radius: 50%;
    background-color: var(--Notification);
}
</style>
